import { useNavigate } from "react-router-dom";
import { useRef, useEffect, useCallback } from "react";
import { message } from "antd";
import { useFoodManagement } from "../hooks/useFoodManagement";
import { useMealPhotoAnalyze } from "../hooks/useMealPhotoAnalyze";
import GluceraNavigationRoutes from "../utils/navigationRoutes";
import {
  Home,
  UtensilsCrossed,
  Scale,
  User,
  Camera,
  TrendingUp,
} from "lucide-react";
import MealAnalyzingProgressOverlay from "./MealAnalyzingProgressOverlay";

const BottomNavBar = ({ activeIndex = 0 }) => {
  const navigate = useNavigate();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const { setUploadedImage } = useFoodManagement();
  const { analyzeMealPhoto, isAnalyzing, uploadProgress } = useMealPhotoAnalyze();

  /** Full-screen meal camera (Camera · Gallery · Barcode) — same as before */
  const openMealCameraPage = useCallback(() => {
    navigate("/glucera" + GluceraNavigationRoutes.MEAL_CAMERA);
  }, [navigate]);

  useEffect(() => {
    const onOpenMealCamera = () => openMealCameraPage();
    const onTriggerCamera = () => cameraInputRef.current?.click();
    const onTriggerGallery = () => galleryInputRef.current?.click();
    window.addEventListener("glucera-open-meal-camera", onOpenMealCamera);
    window.addEventListener("glucera-trigger-camera", onTriggerCamera);
    window.addEventListener("glucera-trigger-gallery", onTriggerGallery);
    return () => {
      window.removeEventListener("glucera-open-meal-camera", onOpenMealCamera);
      window.removeEventListener("glucera-trigger-camera", onTriggerCamera);
      window.removeEventListener("glucera-trigger-gallery", onTriggerGallery);
    };
  }, [openMealCameraPage]);

  const handleButtonClick = () => openMealCameraPage();

  const handleFileUpload = (file: File) => {
    if (file && file.type.startsWith("image/")) {
      // Create blob URL for immediate display
      const blobUrl = URL.createObjectURL(file);
      setUploadedImage(blobUrl);

      // Also convert to base64 for persistence
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        localStorage.setItem("glucera_uploaded_image", base64String);
      };
      reader.readAsDataURL(file);

      analyzeMealPhoto(file);
    } else {
      message.error("Please select a valid image file");
    }
  };

  return (
    <>
      {isAnalyzing && (
        <MealAnalyzingProgressOverlay uploadProgress={uploadProgress} />
      )}

      {/* Bottom Navigation Bar - Hidden during upload */}
      {!isAnalyzing && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-background pb-[max(8px,env(safe-area-inset-bottom,0px))]">
          <div className="mx-auto max-w-md bg-background px-4 pb-2">
          <div className="relative rounded-2xl border border-border/50 bg-card shadow-lg">
            <div className="grid grid-cols-5 items-center gap-1 px-2 py-2.5">
              {/* Home */}
              <div
                className="flex cursor-pointer flex-col items-center gap-1 rounded-lg px-1 py-1.5 transition-all hover:bg-accent/50 active:scale-95"
                onClick={() =>
                  navigate("/glucera" + GluceraNavigationRoutes.HOMEPAGE)
                }
              >
                <Home
                  className={`h-6 w-6 transition-colors ${activeIndex === 0 ? "text-primary" : "text-muted-foreground"}`}
                />
                <span
                  className={`text-compact-xs font-medium transition-colors ${activeIndex === 0 ? "text-primary" : "text-muted-foreground"}`}
                >
                  Home
                </span>
              </div>

              {/* Trends */}
              <div
                className="flex cursor-pointer flex-col items-center gap-1 rounded-lg px-1 py-1.5 transition-all hover:bg-accent/50 active:scale-95"
                onClick={() =>
                  navigate("/glucera" + GluceraNavigationRoutes.Mealpage)
                }
              >
                <UtensilsCrossed
                  className={`h-6 w-6 transition-colors ${activeIndex === 1 ? "text-primary" : "text-muted-foreground"}`}
                />
                <span
                  className={`text-compact-xs font-medium transition-colors ${activeIndex === 1 ? "text-primary" : "text-muted-foreground"}`}
                >
                  Meals
                </span>
              </div>

              {/* Camera FAB — centered in nav row, floated up like original */}
              <div className="relative flex items-center justify-center">
                <div className="absolute -top-8">
                  <button
                    type="button"
                    aria-label="Log meal — open camera"
                    className={`flex h-14 w-14 items-center justify-center rounded-full border-4 border-background shadow-2xl transition-all duration-300 ${isAnalyzing
                      ? "animate-pulse cursor-not-allowed"
                      : "hover:scale-110 active:scale-95"
                      }`}
                    onClick={handleButtonClick}
                    disabled={isAnalyzing}
                    style={{
                      backgroundColor: isAnalyzing ? "#1E40AF" : "#3B82F6",
                      boxShadow: "0 20px 40px -12px rgba(59, 130, 246, 0.3)",
                    }}
                  >
                    <Camera className="h-6 w-6 text-white drop-shadow-lg" />
                  </button>
                </div>

                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  ref={cameraInputRef}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file);
                    e.target.value = "";
                  }}
                  style={{ display: "none" }}
                />
                <input
                  type="file"
                  accept="image/*"
                  ref={galleryInputRef}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file);
                    e.target.value = "";
                  }}
                  style={{ display: "none" }}
                />
              </div>

              {/* Measure */}
              <div
                className="flex cursor-pointer flex-col items-center gap-1 rounded-lg px-1 py-1.5 transition-all hover:bg-accent/50 active:scale-95"
                onClick={() =>
                  navigate("/glucera" + GluceraNavigationRoutes.CUSTOM_SCREEN)
                }
              >
                <Scale
                  className={`h-6 w-6 transition-colors ${activeIndex === 2 ? "text-primary" : "text-muted-foreground"}`}
                />
                <span
                  className={`text-compact-xs font-medium transition-colors ${activeIndex === 2 ? "text-primary" : "text-muted-foreground"}`}
                >
                  Measure
                </span>
              </div>

              {/* Trends */}
              <div
                className="flex cursor-pointer flex-col items-center gap-1 rounded-lg px-1 py-1.5 transition-all hover:bg-accent/50 active:scale-95"
                onClick={() => navigate("/glucera" + GluceraNavigationRoutes.TRENDS_PAGE)}
              >
                <TrendingUp
                  className={`h-6 w-6 transition-colors ${activeIndex === 3 ? "text-primary" : "text-muted-foreground"}`}
                />
                <span
                  className={`text-compact-xs font-medium transition-colors ${activeIndex === 3 ? "text-primary" : "text-muted-foreground"}`}
                >
                  Trends
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </>
  );
};

export default BottomNavBar;
